import {
	Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, ChangeDetectorRef
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { MessageService } from 'base';
import { NetFacadeService } from 'netview';
import { ShareDto, ShareTypes, ShareAccessTypes } from 'UvExplorerServer.Dtos.TS/Shares';
import { DateService } from 'base';
import { Privileges } from 'UvExplorerServer.Dtos.TS/Auth';

@Component({
	selector: 'share-source',
	templateUrl: './share-source.component.html',
	styleUrls: ['./share-source.component.scss']
})
export class ShareSourceComponent implements OnInit, OnChanges, OnDestroy {

	@Input()
	public sharedItemGuid: string;

	@Input()
	public sourceUserGuid: string;

	public entries: any[];

	public displayShareEditor: boolean;

	public targetShare: ShareDto;
	public displayPrivilegesEditor: boolean;

	private sharesLoadedSub: Subscription;
	private sharesChangedSub: Subscription;

	constructor(private messageService: MessageService,
		private netFacadeService: NetFacadeService,
		private dateService: DateService) {

		this.sharedItemGuid = "";
		this.sourceUserGuid = "";

		this.displayShareEditor = false;

		this.targetShare = null;
		this.displayPrivilegesEditor = false;

		this.rebuildEntries();

		this.sharesLoadedSub = this.netFacadeService.onSharesLoaded.subscribe(() => {
			this.rebuildEntries();
		});
		this.sharesChangedSub = this.netFacadeService.onSharesChanged.subscribe(() => {
			this.rebuildEntries();
		});
	}

	private shareTargetName(share: ShareDto): string {
		return (share.HasTargetUserGuid ? share.TargetUserName : share.TargetGroupName);
	}

	private shareTargetType(share: ShareDto): string {
		return (share.HasTargetUserGuid ? "User" : "Group");
	}

	private formatDateHm(date: string | Date): string {
		return this.dateService.formatDateHm(date);
	}

	public get hasSharedItemGuid(): boolean {
		return !!this.sharedItemGuid;
	}

	public get hasSourceUserGuid(): boolean {
		return !!this.sourceUserGuid;
	}

	public ngOnInit(): void {
		return;
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.rebuildEntries();
	}

	public ngOnDestroy(): void {
		if (this.sharesLoadedSub) {
			this.sharesLoadedSub.unsubscribe();
			this.sharesLoadedSub = undefined;
		}
		if (this.sharesChangedSub) {
			this.sharesChangedSub.unsubscribe();
			this.sharesChangedSub = undefined;
		}
	}

	public get hasEntries(): boolean {
		return (!!this.entries && this.entries.length > 0);
	}

	private rebuildEntries(): void {
		this.entries = [];
		if (this.hasSharedItemGuid) {
			let shares: ShareDto[] = this.netFacadeService.getSourceSharesForItem(this.sharedItemGuid);
			if (shares) {
				shares.forEach((share: ShareDto) => {
					this.entries.push({
						shareTargetName: this.shareTargetName(share),
						shareTargetType: this.shareTargetType(share),
						accessType: share.AccessType,
						modified: this.formatDateHm(share.Modified),
						tag: share,
					});
				});
			}
		}
	}

	public createShare(): void {
		this.displayShareEditor = true;
	}

	public onShareCreated(): void {
		this.rebuildEntries();
	}

	public deleteShare(share: ShareDto): void {
		this.netFacadeService.deleteSourceShare(share.Guid).subscribe(
			(result: boolean) => {
				this.rebuildEntries();
				this.messageService.displayInfoMessage("Share was deleted");
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not delete share");
			});
	}

	public accessChanged(share: ShareDto, accessType: string): void {
		share.AccessType = accessType;
		this.netFacadeService.updateShare(share).subscribe(
			(result: ShareDto) => {
				this.rebuildEntries();
				this.messageService.displayInfoMessage("Share was updated");
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not update share");
			});
	}

	public doCustomSort(event): void {
		let comparer = function (a: any, b: any): number {
			let result = 0;

			switch (event.field) {
				case 'modified':
					{
						let aDate = new Date(a.tag.Modified);
						let bDate = new Date(b.tag.Modified);
						result = ((aDate < bDate) ? -1 : ((aDate > bDate) ? 1 : 0));
					}
				default:
					// Compare string fields
					result = a[event.field].localeCompare(b[event.field]);
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

	public editPrivileges(share: ShareDto): void {
		this.targetShare = share;
		this.displayPrivilegesEditor = true;
	}

	public onPrivilegesEditorOK(): void {
		this.netFacadeService.updateShare(this.targetShare).subscribe(
			(result: ShareDto) => {
				this.rebuildEntries();
				this.messageService.displayInfoMessage("Share was updated");
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not update share");
			});

		this.targetShare = null;
	}

	public onPrivilegesEditorCancel(): void {
		this.targetShare = null;
	}

}
